import React, { useState, useEffect } from 'react';
import { 
  Database, RefreshCw, Download, Upload, Trash2, Edit2, Plus, 
  ChevronRight, ChevronDown, Eye, Save, X, AlertTriangle,
  Server, HardDrive, FileJson, CloudUpload, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const DatabaseAdmin = () => {
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [exportData, setExportData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [collectionsRes, statsRes] = await Promise.all([
        api.get('/db-admin/collections'),
        api.get('/db-admin/stats')
      ]);
      setCollections(collectionsRes.data.collections);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load database info');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCollectionDocuments = async (collectionName) => {
    setSelectedCollection(collectionName);
    setDocsLoading(true);
    try {
      const response = await api.get(`/db-admin/collections/${collectionName}?limit=100`);
      setDocuments(response.data.documents);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setDocsLoading(false);
    }
  };

  const handleDeleteDocument = async (collectionName, docId) => {
    if (!window.confirm(`Are you sure you want to delete document ${docId}?`)) return;
    
    try {
      await api.delete(`/db-admin/collections/${collectionName}/${docId}`);
      toast.success('Document deleted');
      loadCollectionDocuments(collectionName);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDoc(doc);
    setEditContent(JSON.stringify(doc, null, 2));
  };

  const handleSaveDocument = async () => {
    try {
      const updatedDoc = JSON.parse(editContent);
      await api.put(`/db-admin/collections/${selectedCollection}/${editingDoc.id}`, updatedDoc);
      toast.success('Document updated');
      setEditingDoc(null);
      loadCollectionDocuments(selectedCollection);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error('Failed to update document');
      }
    }
  };

  const handleExportDatabase = async () => {
    try {
      const response = await api.get('/db-admin/export/json');
      setExportData(response.data);
      
      // Also trigger download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `database_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Database exported successfully');
    } catch (error) {
      toast.error('Failed to export database');
    }
  };

  const handleImportDatabase = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!window.confirm('This will merge imported data with existing data. Continue?')) {
      event.target.value = '';
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/db-admin/import?merge=true', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Import complete: ${response.data.collections_processed.length} collections processed`);
      loadData();
    } catch (error) {
      toast.error('Failed to import database');
    }
    
    event.target.value = '';
  };

  const handlePrepareProductionSync = async () => {
    setSyncLoading(true);
    try {
      const response = await api.post('/db-admin/sync-to-production');
      toast.success('Production sync export prepared!');
      
      // Show the export data
      const exportRes = await api.get('/db-admin/export/json');
      setExportData(exportRes.data);
      
      // Auto-download
      const blob = new Blob([JSON.stringify(exportRes.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `production_sync_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
    } catch (error) {
      toast.error('Failed to prepare production sync');
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Database size={28} />
              Database Administration
            </h2>
            <p className="text-slate-300 mt-1">
              Full MongoDB database management • Export/Import • Production Sync
            </p>
          </div>
          <button
            onClick={loadData}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Database Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <HardDrive size={16} className="text-blue-600" />
              <span className="text-xs text-blue-600 font-medium">Database</span>
            </div>
            <div className="text-lg font-bold text-blue-700">{stats.database_name}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-700">{stats.total_collections}</div>
            <div className="text-xs text-purple-600">Collections</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.totals.documents}</div>
            <div className="text-xs text-green-600">Total Documents</div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-amber-700">{stats.totals.users}</div>
            <div className="text-xs text-amber-600">Users</div>
          </div>
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-cyan-700">{stats.totals.cases}</div>
            <div className="text-xs text-cyan-600">Cases</div>
          </div>
          <div className="bg-gradient-to-br from-rose-50 to-red-50 border border-rose-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-rose-700">{stats.totals.referrals}</div>
            <div className="text-xs text-rose-600">Referrals</div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleExportDatabase}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Download size={18} />
          Export Database
        </button>
        
        <label className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 cursor-pointer">
          <Upload size={18} />
          Import Database
          <input
            type="file"
            accept=".json"
            onChange={handleImportDatabase}
            className="hidden"
          />
        </label>
        
        <button
          onClick={handlePrepareProductionSync}
          disabled={syncLoading}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {syncLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <CloudUpload size={18} />
          )}
          Sync to Production
        </button>
      </div>

      {/* Production Sync Instructions */}
      {exportData && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-600 mt-1" size={20} />
            <div>
              <h4 className="font-bold text-slate-900">Export Ready for Production Sync</h4>
              <p className="text-sm text-slate-600 mt-1">
                Exported {exportData.total_collections} collections with {exportData.total_documents} documents.
              </p>
              <div className="mt-3 p-3 bg-white rounded-lg border border-purple-100">
                <p className="text-sm font-medium text-slate-700 mb-2">To sync to production:</p>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>The JSON file has been downloaded automatically</li>
                  <li>Contact Emergent Support: <a href="https://discord.gg/VzKfwCXC4A" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Discord</a></li>
                  <li>Request: "Import this database export to my production environment"</li>
                  <li>Provide your Job ID and the downloaded JSON file</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collections List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Collections Panel */}
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Server size={18} />
              Collections ({collections.length})
            </h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {collections.map((coll) => (
              <button
                key={coll.name}
                onClick={() => loadCollectionDocuments(coll.name)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex justify-between items-center ${
                  selectedCollection === coll.name ? 'bg-purple-50 border-l-4 border-l-purple-600' : ''
                }`}
              >
                <span className="font-medium text-slate-700">{coll.name}</span>
                <span className="text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  {coll.document_count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Documents Panel */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <FileJson size={18} />
              {selectedCollection ? `Documents in ${selectedCollection}` : 'Select a collection'}
            </h3>
          </div>
          
          {docsLoading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : selectedCollection ? (
            <div className="max-h-[500px] overflow-y-auto">
              {documents.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No documents in this collection</div>
              ) : (
                documents.map((doc, index) => (
                  <div key={doc.id || index} className="border-b border-slate-100 last:border-0">
                    <div 
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex justify-between items-center"
                      onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}
                    >
                      <div className="flex items-center gap-2">
                        {expandedDoc === doc.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <span className="font-mono text-sm text-slate-600">
                          {doc.id ? doc.id.substring(0, 12) + '...' : `Document ${index + 1}`}
                        </span>
                        {doc.email && <span className="text-sm text-purple-600 ml-2">{doc.email}</span>}
                        {doc.company_name && <span className="text-sm text-blue-600 ml-2">{doc.company_name}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditDocument(doc); }}
                          className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteDocument(selectedCollection, doc.id); }}
                          className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    
                    {expandedDoc === doc.id && (
                      <div className="px-4 pb-4">
                        <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
                          {JSON.stringify(doc, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500">
              Select a collection to view documents
            </div>
          )}
        </div>
      </div>

      {/* Edit Document Modal */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-bold text-slate-900">Edit Document</h3>
              <button onClick={() => setEditingDoc(null)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-4">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-96 font-mono text-sm p-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                spellCheck="false"
              />
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingDoc(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDocument}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseAdmin;
